import { useEffect, useRef, useState } from 'react';

import type { Price, Tier } from '@shared/dream-components';

export const getSelectedTierAndPrice = (
  tiers?: Tier[],
  recommendedTierId?: string,
  recommendedPriceId?: string,
): { selectedTier?: Tier; selectedPrice?: Price } => {
  if (!tiers) return {};
  if (Array.isArray(tiers) && tiers.length === 0) return {};

  // If there's a recommended price ID but no tier ID, search across all tiers for that price
  if (recommendedPriceId && !recommendedTierId) {
    for (const tier of tiers) {
      const recommendedPrice = tier.prices.find((price) => price.id === recommendedPriceId);
      if (recommendedPrice) {
        return {
          selectedTier: tier,
          selectedPrice: recommendedPrice,
        };
      }
    }
  }

  // If there's a recommended tier ID, try to find it
  if (recommendedTierId) {
    const recommendedTier = tiers.find((tier) => tier.id === recommendedTierId);
    if (recommendedTier) {
      // If there's a recommended price ID, try to find it within the recommended tier
      if (recommendedPriceId) {
        const recommendedPrice = recommendedTier.prices.find((price) => price.id === recommendedPriceId);
        if (recommendedPrice) {
          return {
            selectedTier: recommendedTier,
            selectedPrice: recommendedPrice,
          };
        }
      }

      // If no specific price is recommended, use the first price of the recommended tier
      if (Array.isArray(recommendedTier.prices) && recommendedTier.prices.length > 0) {
        return {
          selectedTier: recommendedTier,
          selectedPrice: recommendedTier.prices[0],
        };
      }

      return { selectedTier: recommendedTier };
    }
  }

  // Fallback to first tier if no recommended tier is found or specified
  const firstTier = tiers[0];
  if (Array.isArray(firstTier.prices) && firstTier.prices.length === 0) return { selectedTier: firstTier };

  return {
    selectedTier: firstTier,
    selectedPrice: firstTier.prices[0],
  };
};

// Resolves and keeps the selected tier/price in sync as `tiers` changes. On mount
// `tiers` is often [] (API not yet loaded), so we re-run when it arrives. Once a
// selection exists, we refresh it from the new list to pick up enriched fields
// (e.g. premium_offer data) without dropping the user's choice.
export const useResolvedTierSelection = (tiers: Tier[], recommendedTierId?: string, recommendedPriceId?: string) => {
  const [initial] = useState(() => getSelectedTierAndPrice(tiers, recommendedTierId, recommendedPriceId));
  const [selectedTier, setSelectedTier] = useState<Tier | undefined>(initial.selectedTier);
  const [selectedPrice, setSelectedPrice] = useState<Price | undefined>(
    initial.selectedPrice ?? initial.selectedTier?.prices[0],
  );

  const selectedTierRef = useRef(selectedTier);
  const selectedPriceRef = useRef(selectedPrice);
  selectedTierRef.current = selectedTier;
  selectedPriceRef.current = selectedPrice;

  useEffect(() => {
    const currentTier = selectedTierRef.current;
    const currentPrice = selectedPriceRef.current;

    const { selectedTier: resolvedTier, selectedPrice: resolvedPrice } = getSelectedTierAndPrice(
      tiers,
      recommendedTierId,
      recommendedPriceId,
    );

    if (!currentTier && !currentPrice) {
      if (resolvedTier) setSelectedTier(resolvedTier);
      setSelectedPrice(resolvedPrice || resolvedTier?.prices[0]);
    } else if (currentTier && currentPrice) {
      const updatedTier = tiers.find((t) => t.id === currentTier.id);
      if (updatedTier) {
        const updatedPrice = updatedTier.prices.find((p) => p.id === currentPrice.id);
        if (updatedPrice) {
          setSelectedTier(updatedTier);
          setSelectedPrice(updatedPrice);
        }
      }
    }
  }, [tiers, recommendedTierId, recommendedPriceId]);

  return { selectedTier, setSelectedTier, selectedPrice, setSelectedPrice };
};
